import React, {createContext, useContext, useMemo} from 'react'
import {useTownhallTemplate} from 'Event/template/Townhall'
import {orderedIdsByPosition} from 'lib/list'
import {Template} from 'Event/template'
import {DeepRequired} from 'lib/type-utils'

type PostFormStylesContextValue = {
  postFormStyles: DeepRequired<Template>['postFormStyles']
  postStyles: DeepRequired<Template>['postStyles']
}

const PostFormStylesContext = createContext<
  PostFormStylesContextValue | undefined
>(undefined)

export function usePostFormStyles() {
  return useContext(PostFormStylesContext)
}

export default function TownhallPostFormStylesProvider(props: {
  children: React.ReactNode
}) {
  const template = useTownhallTemplate()

  const value = useMemo((): PostFormStylesContextValue => {
    const {dashboardSections} = template

    const orderedIds = orderedIdsByPosition(dashboardSections)
    const firstBlogSectionId = orderedIds.find(
      (id) => dashboardSections[id].name === 'blog',
    )

    if (!firstBlogSectionId) {
      return {
        postFormStyles: template.postFormStyles,
        postStyles: template.postStyles,
      }
    }

    const blogSection = dashboardSections[firstBlogSectionId]

    // Type guard: check if this section has postFormStyles (only BlogSection does)
    const sectionPostFormStyles =
      'postFormStyles' in blogSection ? blogSection.postFormStyles : undefined
    const sectionPostStyles =
      'postStyles' in blogSection ? blogSection.postStyles : undefined

    return {
      postFormStyles: {
        width: sectionPostFormStyles?.width ?? template.postFormStyles.width,
        position:
          sectionPostFormStyles?.position ?? template.postFormStyles.position,
        buttonSize:
          sectionPostFormStyles?.buttonSize ??
          template.postFormStyles.buttonSize,
        buttonRadius:
          sectionPostFormStyles?.buttonRadius ??
          template.postFormStyles.buttonRadius,
        buttonColor:
          sectionPostFormStyles?.buttonColor ??
          template.postFormStyles.buttonColor,
        buttonBackgroundColor:
          sectionPostFormStyles?.buttonBackgroundColor ??
          template.postFormStyles.buttonBackgroundColor,
        buttonHoverBackgroundColor:
          sectionPostFormStyles?.buttonHoverBackgroundColor ??
          template.postFormStyles.buttonHoverBackgroundColor,
        buttonPosition:
          sectionPostFormStyles?.buttonPosition ??
          template.postFormStyles.buttonPosition,
        buttonFontSize:
          sectionPostFormStyles?.buttonFontSize ??
          template.postFormStyles.buttonFontSize,
        buttonBorderWidth:
          sectionPostFormStyles?.buttonBorderWidth ??
          template.postFormStyles.buttonBorderWidth,
        buttonBorderColor:
          sectionPostFormStyles?.buttonBorderColor ??
          template.postFormStyles.buttonBorderColor,
        inputStyles: {
          ...template.postFormStyles.inputStyles,
          ...sectionPostFormStyles?.inputStyles,
        },
      },
      postStyles: {
        titleFont:
          sectionPostStyles?.titleFont ?? template.postStyles.titleFont,
        titleTextColor:
          sectionPostStyles?.titleTextColor ??
          template.postStyles.titleTextColor,
        titleFontSize:
          sectionPostStyles?.titleFontSize ?? template.postStyles.titleFontSize,
        dateTextColor:
          sectionPostStyles?.dateTextColor ?? template.postStyles.dateTextColor,
        dateFontSize:
          sectionPostStyles?.dateFontSize ?? template.postStyles.dateFontSize,
        contentFontSize:
          sectionPostStyles?.contentFontSize ??
          template.postStyles.contentFontSize,
        contentTextColor:
          sectionPostStyles?.contentTextColor ??
          template.postStyles.contentTextColor,
        spacing: sectionPostStyles?.spacing ?? template.postStyles.spacing,
      },
    }
  }, [template])

  return (
    <PostFormStylesContext.Provider value={value}>
      {props.children}
    </PostFormStylesContext.Provider>
  )
}
